// walkthrough/highlight.js — K843: the highlight adapter.
//
// The guide engine owns step state, authorization, completion, context,
// recovery and telemetry. This file owns PRESENTATION ONLY: an outline on
// the target, a pointing hand aimed at it, an anchored tooltip card next to
// it, and scrolling it into view.
//
// WHY THIS IS OURS AND NOT DRIVER.JS
//   Spec section 6 makes one boundary hard: "Driver.js renders HTML in
//   popover title and description. Raw model output must never reach those
//   fields." Driver.js's API takes HTML there, so honouring that boundary
//   means maintaining a discipline on every call site forever. Here there
//   ARE no HTML fields - every slot is assigned with textContent, so the
//   boundary is structural rather than remembered. In the one product whose
//   pitch is verified claims, a guarantee beats a convention.
//
//   It also removes the two dependency questions section 8 left open: no
//   native-popover/top-layer behaviour to establish, and no published
//   artifact to verify. (The provenance question resolved benignly: the
//   nilbuild account holds developer-roadmap, design-patterns-for-humans,
//   git-standup, pennywise and githunt - Kamran Ahmed's whole portfolio -
//   and GitHub renames carry repos and redirect old URLs, which is why
//   kamranahmedse/driver.js still resolves. A rename, not a takeover. The
//   evidence is the portfolio, not the display name.)
//
//   Spec section 8 also says what v1 actually needs: "A reliable outline and
//   an anchored pointer are enough for v1. Do not delay the release over
//   dimming effects." That is this file. The adapter seam stays, so Driver.js
//   can be dropped in later for the dimming polish without touching the
//   engine.
//
// POSITIONING, AND THE BUG WE SIDESTEP
//   Driver.js had to ship fixes for targets inside scroll containers and
//   under transform/filter ancestors. We avoid that class of bug rather than
//   fixing it: getBoundingClientRect() is viewport-relative and already
//   accounts for every ancestor transform, and the ring, hand and card are
//   position:fixed in the same viewport space. No offsetParent arithmetic,
//   so no ancestor to get wrong.
//
// ACCESSIBILITY, the parts libraries get wrong (spec section 9)
//   * Non-modal. Focus is NEVER trapped and the page is never made inert -
//     the user has to reach the real control, including with a screen reader.
//   * NO full-page dim. A 0.5-opacity scrim is what washes out a focus ring
//     and fails 1.4.11 Non-text Contrast. An outline at adequate contrast
//     does the same job without competing with the focus indicator.
//   * 2.4.11 Focus Not Obscured: the card is placed on whichever side has
//     room and never over the target. If no side has room it docks to a
//     corner away from the target rather than covering it. The hand obeys
//     the same rule and HIDES rather than overlap the control - see below.
//   * prefers-reduced-motion disables the bob, the pulse, the card
//     transition and smooth scroll. Nothing moves.
//   * 2.2.2 Pause, Stop, Hide: the bob and the ring pulse are motion that
//     runs for longer than five seconds alongside other content, so they
//     need a pause/stop/hide mechanism. The card's Stop action is that
//     mechanism - it ends the session and removes both. It is therefore
//     load-bearing for conformance, not just convenience: if a caller ever
//     renders this without a dismiss path, the motion has to go too.
//   * This file deliberately contains NO live region. Spec section 9: a
//     concise region announces step-ready/blocked/failed, and there is no
//     token-by-token narration. That region belongs to the host, which knows
//     the engine state; an adapter firing its own announcements would double
//     up with it.

const RING_ID = 'g360-wt-ring';
const CARD_ID = 'g360-wt-card';
const HAND_ID = 'g360-wt-hand';

/* The hand's box is SQUARE on purpose. The three orientations are one piece
   of art rotated, and rotating a square leaves its bounding box unchanged -
   so the placement arithmetic below is the same three constants in every
   orientation instead of a width/height swap waiting to be got wrong. */
const HAND_BOX = 32;
/* Clearance between the ring edge and the fingertip. Must exceed BOB_PX, or
   at the top of the bob the fingertip crosses the ring and the hand covers
   the very control the user is being asked to click (2.4.11). */
const HAND_GAP = 10;
const BOB_PX = 7;

const SVG_NS = 'http://www.w3.org/2000/svg';

function reducedMotion() {
  try {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}

/* An app dialog that legitimately contains focus. Spec section 9: when the
   application's own modal opens we pause external coach controls rather than
   forcing ourselves above it. */
export function appDialogOpen() {
  try {
    if (document.querySelector('dialog[open]')) return true;
    const modals = document.querySelectorAll('[role="dialog"],[role="alertdialog"]');
    for (const m of modals) {
      if (m.getAttribute('aria-hidden') === 'true') continue;
      const r = m.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return true;
    }
    return false;
  } catch { return false; }
}

function el(tag, cls) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  return n;
}

function svgEl(tag, attrs) {
  const n = document.createElementNS(SVG_NS, tag);
  for (const k of Object.keys(attrs || {})) n.setAttribute(k, String(attrs[k]));
  return n;
}

/* THE POINTING HAND.
   Built with createElementNS, NOT by assigning an SVG string to innerHTML -
   that is one of the five sinks scripts/check-walkthrough-no-html.mjs fails
   the build on, and the guarantee in this file is structural rather than
   remembered.

   Composed from three rounded primitives rather than one hand-written path
   because a path with a wrong number in it renders as an unrecognisable blob
   and nobody notices until a customer does. Drawn fist, thumb, then index on
   top: the white stroke on each shape then reads as the separation between
   them, which is what makes the silhouette legible as a hand at 32px.

   Accent fill, white stroke. Accent ties the hand to the ring so the eye
   reads them as one object pointing at one control; the white stroke keeps
   the silhouette visible whatever the accent is sitting on, in either theme.

   In the unrotated art the fingertip is at the TOP of the box, horizontally
   at the box centre (the index rect spans 10.6-15.4 of 26, centre 13). Every
   placement below depends on that, which is why it is stated here. */
function buildHand() {
  const root = svgEl('svg', {
    viewBox: '0 0 26 26', width: HAND_BOX, height: HAND_BOX,
    'aria-hidden': 'true', focusable: 'false',
  });
  const g = svgEl('g', {
    fill: 'var(--g-accent,#4f7cff)', stroke: '#fff',
    'stroke-width': 1.6, 'stroke-linejoin': 'round',
  });
  // fist
  g.appendChild(svgEl('rect', { x: 8.2, y: 11, width: 9.6, height: 13, rx: 4.8 }));
  // thumb, rotated about its own centre (7.1, 17.4)
  g.appendChild(svgEl('rect', {
    x: 4.6, y: 13.6, width: 5, height: 7.6, rx: 2.5,
    transform: 'rotate(-20 7.1 17.4)',
  }));
  // index finger, last so it sits over the fist
  g.appendChild(svgEl('rect', { x: 10.6, y: 2, width: 4.8, height: 13, rx: 2.4 }));
  root.appendChild(g);
  return root;
}

function injectStyle() {
  if (document.getElementById('g360-wt-style')) return;
  const s = document.createElement('style');
  s.id = 'g360-wt-style';
  /* Static, author-written CSS. No interpolation of any kind reaches this
     string - it is a constant, so there is nothing to escape. */
  s.textContent = [
    '#' + RING_ID + '{position:fixed;z-index:2147483000;pointer-events:none;',
    'border:2px solid var(--g-accent,#4f7cff);border-radius:6px;',
    'box-shadow:0 0 0 2px rgba(255,255,255,.9),0 0 0 4px rgba(79,124,255,.35);}',
    '#' + RING_ID + '.g360-wt-anim{transition:top .18s ease,left .18s ease,width .18s ease,height .18s ease;',
    'animation:g360-wt-pulse 1.9s ease-in-out infinite;}',
    /* The pulse animates the OUTER glow only. The 2px solid border never
       changes, so the ring's own contrast stays constant and 1.4.11 does not
       depend on which frame of the animation you measure. */
    '@keyframes g360-wt-pulse{',
    '0%,100%{box-shadow:0 0 0 2px rgba(255,255,255,.9),0 0 0 4px rgba(79,124,255,.35);}',
    '50%{box-shadow:0 0 0 2px rgba(255,255,255,.9),0 0 0 8px rgba(79,124,255,.10);}}',

    '#' + HAND_ID + '{position:fixed;z-index:2147483002;pointer-events:none;line-height:0;',
    /* pointer-events:none is not cosmetic. A hand floating over the page that
       swallowed clicks would block the exact click the step is asking for. */
    'filter:drop-shadow(0 2px 5px rgba(16,24,40,.35));}',
    /* The rotation is baked into each keyframe because transform is not
       additive: a rotate on the element plus a translate in the animation
       would overwrite each other. In the rotated frame local -Y points at the
       target in all three cases, so one bob shape serves all of them. */
    '#' + HAND_ID + '.g360-wt-up{animation:g360-wt-bob-up 1.4s ease-in-out infinite;}',
    '#' + HAND_ID + '.g360-wt-right{animation:g360-wt-bob-right 1.4s ease-in-out infinite;}',
    '#' + HAND_ID + '.g360-wt-left{animation:g360-wt-bob-left 1.4s ease-in-out infinite;}',
    '@keyframes g360-wt-bob-up{0%,100%{transform:translateY(0);}',
    '50%{transform:translateY(-' + BOB_PX + 'px);}}',
    '@keyframes g360-wt-bob-right{0%,100%{transform:rotate(90deg) translateY(0);}',
    '50%{transform:rotate(90deg) translateY(-' + BOB_PX + 'px);}}',
    '@keyframes g360-wt-bob-left{0%,100%{transform:rotate(-90deg) translateY(0);}',
    '50%{transform:rotate(-90deg) translateY(-' + BOB_PX + 'px);}}',
    /* Reduced motion keeps the hand and the ring - they are the signal - and
       removes only the movement. The rotation has to be restated here, or the
       hand would point up while sitting beside the control. */
    '@media (prefers-reduced-motion: reduce){',
    '#' + RING_ID + '.g360-wt-anim{transition:none;animation:none;}',
    '#' + HAND_ID + '.g360-wt-up{animation:none;transform:none;}',
    '#' + HAND_ID + '.g360-wt-right{animation:none;transform:rotate(90deg);}',
    '#' + HAND_ID + '.g360-wt-left{animation:none;transform:rotate(-90deg);}}',

    '#' + CARD_ID + '{position:fixed;z-index:2147483001;max-width:340px;min-width:240px;',
    'background:var(--g-surface,#fff);color:var(--g-text-1,#111);',
    'border:1px solid var(--g-border,#d6d9e0);border-radius:10px;',
    'box-shadow:0 8px 28px rgba(16,24,40,.18);padding:14px 16px;font-size:13.5px;line-height:1.5;}',
    '#' + CARD_ID + ' .g360-wt-step{font-size:11px;letter-spacing:.04em;text-transform:uppercase;',
    'color:var(--g-text-3,#667085);margin-bottom:6px;}',
    '#' + CARD_ID + ' .g360-wt-title{font-weight:600;font-size:14px;margin-bottom:6px;}',
    '#' + CARD_ID + ' .g360-wt-body{white-space:pre-wrap;}',
    '#' + CARD_ID + ' .g360-wt-row{display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;}',
    '#' + CARD_ID + ' button{font:inherit;padding:6px 12px;border-radius:7px;cursor:pointer;',
    'border:1px solid var(--g-border,#d6d9e0);background:var(--g-surface-2,#f7f8fa);color:inherit;}',
    '#' + CARD_ID + ' button:focus-visible{outline:2px solid var(--g-accent,#4f7cff);outline-offset:2px;}',
    /* The caret is what makes the card read as a tooltip FOR this control
       rather than a panel that happens to be near it. Rotated square with the
       two inward edges dropped, so only the outward edges carry the border. */
    '.g360-wt-caret{position:absolute;width:10px;height:10px;transform:rotate(45deg);',
    'background:var(--g-surface,#fff);border:1px solid var(--g-border,#d6d9e0);}',
  ].join('');
  document.head.appendChild(s);
}

/* Which card side the hand has taken, so the card can get out of its way. */
const HAND_BLOCKS = { up: 'bottom', right: 'left', left: 'right' };

/* Place the card on a side with room, never over the target (2.4.11).
   Returns the side it chose, or null when it had to dock to a corner - the
   caller uses that to decide whether a caret would be pointing at anything.

   `avoid` is the card side the hand is occupying. THE HAND GETS FIRST CLAIM
   ON ITS SIDE: it has to sit within ~10px of the control to read as pointing
   at it, whereas the card is text and reads fine from any adjacent side. So
   the card tries every other side first and only falls back to the hand's,
   with enough extra padding to clear both the hand and the caret.
   This is not hypothetical tidying. The first render put the card 12px below
   the control and the hand 13px below it, so the fingertip sat across the
   card's "STEP 1 OF 5" line. The hand-vs-control and card-vs-control
   assertions both passed; only the screenshot showed it. */
function placeCard(card, rect, avoid) {
  const base = 12;
  const vw = window.innerWidth, vh = window.innerHeight;
  const cw = card.offsetWidth || 300, ch = card.offsetHeight || 140;
  const order = ['bottom', 'top', 'right', 'left'].filter((s) => s !== avoid);
  if (avoid) order.push(avoid);

  for (const side of order) {
    // +12 past the hand box so the card's 6px caret clears the hand too.
    const pad = side === avoid ? base + HAND_BOX + 12 : base;
    let top, left;
    if (side === 'bottom') {
      if (vh - rect.bottom - pad < ch) continue;
      top = rect.bottom + pad;
      left = Math.min(Math.max(base, rect.left), vw - cw - base);
    } else if (side === 'top') {
      if (rect.top - pad < ch) continue;
      top = rect.top - ch - pad;
      left = Math.min(Math.max(base, rect.left), vw - cw - base);
    } else if (side === 'right') {
      if (vw - rect.right - pad < cw) continue;
      left = rect.right + pad;
      top = Math.min(Math.max(base, rect.top), vh - ch - base);
    } else {
      if (rect.left - pad < cw) continue;
      left = rect.left - cw - pad;
      top = Math.min(Math.max(base, rect.top), vh - ch - base);
    }
    card.style.top = Math.round(top) + 'px';
    card.style.left = Math.round(left) + 'px';
    return { side, top, left, cw, ch };
  }

  /* Nowhere beside it has room. Dock to the corner FURTHEST from the target
     rather than covering it - an obscured control is worse than an
     inconveniently placed card. No caret: it is not adjacent to anything. */
  const farTop = rect.top + rect.height / 2 < vh / 2;
  const top = farTop ? vh - ch - base : base;
  const farLeft = rect.left + rect.width / 2 < vw / 2;
  const left = farLeft ? vw - cw - base : base;
  card.style.top = Math.round(top) + 'px';
  card.style.left = Math.round(left) + 'px';
  return { side: null, top, left, cw, ch };
}

/* Point the caret at the target from whichever edge faces it. Every one of
   the four offsets is reset on each call: a stale `top` left over from a
   previous side is how a caret ends up floating in the middle of a card. */
function placeCaret(caret, pos, rect) {
  if (!pos.side) { caret.style.display = 'none'; return; }
  caret.style.display = 'block';
  caret.style.top = ''; caret.style.bottom = '';
  caret.style.left = ''; caret.style.right = '';
  caret.style.borderTopWidth = '1px'; caret.style.borderRightWidth = '1px';
  caret.style.borderBottomWidth = '1px'; caret.style.borderLeftWidth = '1px';

  const tipX = rect.left + rect.width / 2;
  const tipY = rect.top + rect.height / 2;
  // Clamped so the caret stays on the card's edge rather than off its corner.
  const alongX = Math.round(Math.min(Math.max(14, tipX - pos.left), pos.cw - 24));
  const alongY = Math.round(Math.min(Math.max(14, tipY - pos.top), pos.ch - 24));

  if (pos.side === 'bottom') {            // card below target -> caret on top
    caret.style.top = '-6px'; caret.style.left = alongX + 'px';
    caret.style.borderRightWidth = '0'; caret.style.borderBottomWidth = '0';
  } else if (pos.side === 'top') {        // card above -> caret on bottom
    caret.style.bottom = '-6px'; caret.style.left = alongX + 'px';
    caret.style.borderTopWidth = '0'; caret.style.borderLeftWidth = '0';
  } else if (pos.side === 'right') {      // card right of target -> caret left
    caret.style.left = '-6px'; caret.style.top = alongY + 'px';
    caret.style.borderTopWidth = '0'; caret.style.borderRightWidth = '0';
  } else {                                // card left of target -> caret right
    caret.style.right = '-6px'; caret.style.top = alongY + 'px';
    caret.style.borderBottomWidth = '0'; caret.style.borderLeftWidth = '0';
  }
}

/* Put the hand beside the control with its fingertip aimed at the ring.
   Preference order is below-pointing-up first, because that is the reading
   every onboarding tour has trained people on and it is never upside down;
   then from the left pointing right, then from the right pointing left. All
   three are a natural hand - there is deliberately NO downward orientation,
   which is the one that looks like a mistake.

   If no side has the clearance, the hand HIDES. An overlapping hand is worse
   than no hand: it covers the control the step is asking the user to use. */
function placeHand(hand, rect) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const edge = 3 + HAND_GAP;          // ring sits 3px outside the target rect
  const need = edge + HAND_BOX;
  hand.classList.remove('g360-wt-up', 'g360-wt-right', 'g360-wt-left');

  let top, left, dir;
  if (rect.bottom + need <= vh) {
    dir = 'up';
    top = rect.bottom + edge;
    left = rect.left + rect.width / 2 - HAND_BOX / 2;
  } else if (rect.left - need >= 0) {
    dir = 'right';
    left = rect.left - need;
    top = rect.top + rect.height / 2 - HAND_BOX / 2;
  } else if (rect.right + need <= vw) {
    dir = 'left';
    left = rect.right + edge;
    top = rect.top + rect.height / 2 - HAND_BOX / 2;
  } else {
    hand.style.display = 'none';
    return null;
  }
  // Keep it on screen on the other axis without letting it drift onto the
  // control: clamping the ALONG axis only, never the pointing axis.
  if (dir === 'up') left = Math.min(Math.max(2, left), vw - HAND_BOX - 2);
  else top = Math.min(Math.max(2, top), vh - HAND_BOX - 2);

  hand.style.display = 'block';
  hand.style.top = Math.round(top) + 'px';
  hand.style.left = Math.round(left) + 'px';
  hand.classList.add('g360-wt-' + dir);
  return dir;
}

export function createHighlighter() {
  injectStyle();
  let ring = null;
  let card = null;
  let caret = null;
  let hand = null;
  let target = null;
  let raf = 0;
  let onEscape = null;

  function ensure() {
    if (!ring) {
      ring = el('div');
      ring.id = RING_ID;
      if (!reducedMotion()) ring.classList.add('g360-wt-anim');
      document.body.appendChild(ring);
    }
    if (!hand) {
      hand = el('div');
      hand.id = HAND_ID;
      /* Decorative, and deliberately hidden from assistive tech. The card
         already says what to do in words and the host announces it; a node
         that read out as "image" between the step text and the control would
         be noise, not help. */
      hand.setAttribute('aria-hidden', 'true');
      hand.appendChild(buildHand());
      document.body.appendChild(hand);
    }
    if (!card) {
      card = el('div');
      card.id = CARD_ID;
      /* Not role="dialog": a dialog implies modality and invites focus
         trapping. This is a labelled group the user can ignore. */
      card.setAttribute('role', 'group');
      card.setAttribute('aria-label', 'Walkthrough step');
      caret = el('div', 'g360-wt-caret');
      caret.setAttribute('aria-hidden', 'true');
      document.body.appendChild(card);
    }
  }

  function track() {
    if (!target || !ring) return;
    const r = target.getBoundingClientRect();
    // The target scrolled out of the viewport or was unmounted.
    if (r.width === 0 && r.height === 0) {
      ring.style.display = 'none';
      if (hand) hand.style.display = 'none';
      return;
    }
    ring.style.display = 'block';
    ring.style.top = Math.round(r.top - 3) + 'px';
    ring.style.left = Math.round(r.left - 3) + 'px';
    ring.style.width = Math.round(r.width + 6) + 'px';
    ring.style.height = Math.round(r.height + 6) + 'px';
    // Hand first: it claims its side, then the card is placed around it.
    const dir = hand ? placeHand(hand, r) : null;
    if (card) {
      const pos = placeCard(card, r, HAND_BLOCKS[dir] || null);
      if (caret) placeCaret(caret, pos, r);
    }
    raf = window.requestAnimationFrame(track);
  }

  function keyHandler(ev) {
    if (ev.key !== 'Escape') return;
    /* The nested-overlay keyboard contract, stated explicitly because leaving
       it implicit means Escape closes the app's dialog AND the walkthrough and
       the user cannot tell which. While an app dialog is open, Escape is the
       dialog's. */
    if (appDialogOpen()) return;
    if (typeof onEscape === 'function') { ev.stopPropagation(); onEscape(); }
  }

  return {
    /* Every text slot is assigned with textContent. There is no innerHTML in
       this file, which is what makes spec section 6 structural here. */
    show({ element, title, body, stepNumber, stepTotal, actions, onDismiss }) {
      ensure();
      target = element || null;
      onEscape = onDismiss || null;

      card.replaceChildren();
      card.appendChild(caret);

      if (stepNumber && stepTotal) {
        const s = el('div', 'g360-wt-step');
        s.textContent = 'Step ' + stepNumber + ' of ' + stepTotal;
        card.appendChild(s);
      }
      if (title) {
        const t = el('div', 'g360-wt-title');
        t.textContent = String(title);
        card.appendChild(t);
      }
      if (body) {
        const b = el('div', 'g360-wt-body');
        b.textContent = String(body);
        card.appendChild(b);
      }
      const list = Array.isArray(actions) ? actions : [];
      if (list.length) {
        const row = el('div', 'g360-wt-row');
        for (const a of list) {
          const btn = el('button');
          btn.type = 'button';
          btn.textContent = String(a.label);
          if (a.describe) btn.setAttribute('aria-label', String(a.describe));
          btn.addEventListener('click', a.onClick);
          row.appendChild(btn);
        }
        card.appendChild(row);
      }
      card.style.display = 'block';

      if (target) {
        try {
          target.scrollIntoView({
            block: 'center', inline: 'nearest',
            behavior: reducedMotion() ? 'auto' : 'smooth',
          });
        } catch { target.scrollIntoView(); }
        if (raf) window.cancelAnimationFrame(raf);
        track();
      } else {
        /* A step with nothing to point at still gets its card - but neither
           the ring nor the hand, because pointing at nothing is a lie about
           where the control is, and the caret would point at nothing too. */
        if (ring) ring.style.display = 'none';
        if (hand) hand.style.display = 'none';
        if (caret) caret.style.display = 'none';
      }
      document.addEventListener('keydown', keyHandler, true);
    },

    hide() {
      if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
      target = null;
      if (ring) ring.style.display = 'none';
      if (hand) hand.style.display = 'none';
      if (card) { card.style.display = 'none'; card.replaceChildren(); }
      document.removeEventListener('keydown', keyHandler, true);
    },

    destroy() {
      this.hide();
      if (ring && ring.parentNode) ring.parentNode.removeChild(ring);
      if (hand && hand.parentNode) hand.parentNode.removeChild(hand);
      if (card && card.parentNode) card.parentNode.removeChild(card);
      ring = null; card = null; caret = null; hand = null; onEscape = null;
    },

    appDialogOpen,
  };
}
